import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { Questionnaire } from "@/components/Questionnaire";
import { Reveal } from "@/components/Motion";
import { priceRanges } from "@/data";
import { LeadDialog } from "@/components/LeadForm";
import { Button } from "@/components/ui/button";

const STEPS = [
  { key: "counties", type: "multi", question: "Which counties interest you?", options: ["Fulton", "Gwinnett", "DeKalb", "Walton", "Not sure yet"] },
  { key: "cities", type: "text", question: "Which cities or communities interest you?", placeholder: "e.g. Alpharetta, Decatur, Suwanee", help: "Optional — list any areas you have in mind." },
  { key: "price_range", type: "options", question: "What is your desired price range?", options: priceRanges },
  { key: "property_type", type: "options", question: "What type of property do you want?", options: ["Single Family", "Townhome", "Condo", "New Construction", "Multi-family / Investment"] },
  { key: "beds", type: "options", question: "How many bedrooms do you need?", options: ["1", "2", "3", "4", "5+"] },
  { key: "baths", type: "options", question: "How many bathrooms do you need?", options: ["1", "2", "3", "4+"] },
  { key: "timeline", type: "options", question: "When would you like to move?", options: ["Immediately", "Within 30 days", "1–3 months", "3–6 months", "6–12 months", "More than one year", "Just exploring"] },
  { key: "housing_status", type: "options", question: "Do you currently rent, own, or live with family?", options: ["Rent", "Own", "Live with family"] },
  { key: "preapproved", type: "options", question: "Have you been preapproved?", options: ["Yes, pre-approved", "In progress", "Not yet"] },
  { key: "lender_intro", type: "options", question: "Would you like a lender introduction?", options: ["Yes, please", "Maybe later", "No thanks"] },
  { key: "need_to_sell", type: "options", question: "Do you need to sell a home before buying?", options: ["Yes", "No", "Not sure"] },
  { key: "features", type: "multi", question: "What features are most important to you?", options: ["Finished basement", "Primary on main", "Large yard / acreage", "New construction", "No HOA", "One-story", "Pool", "Garage"] },
  { key: "obstacle", type: "options", question: "What has prevented you from buying so far?", options: ["Credit concerns", "Down-payment concerns", "Interest rates", "Monthly payment concerns", "Need to sell first", "Unsure where to start", "Still comparing areas", "Not ready to speak with a lender"] },
  { key: "contact_pref", type: "options", question: "How would you prefer to be contacted?", options: ["Email", "Phone", "Text"] },
  { key: "contact", type: "contact", question: "Last step — how can Richard reach you?", help: "We'll use this to send your personalized plan." },
];

function readiness(ans) {
  const t = ans.timeline || "";
  const pre = ans.preapproved || "";
  if (pre.includes("pre-approved") && (t === "Immediately" || t === "Within 30 days")) return "Ready to Buy";
  if (pre.includes("progress") || t === "1–3 months") return "Getting Ready";
  if (t === "Just exploring" || t === "More than one year") return "Exploring";
  return "Preparing";
}

export default function BuyerQuiz() {
  const [result, setResult] = useState(null);
  if (!result) return <Questionnaire steps={STEPS} leadType="buyer" source="buyer-questionnaire" onComplete={setResult} />;

  const cat = readiness(result);
  const counties = (result.counties || []).filter((c) => c !== "Not sure yet");
  return (
    <div className="bg-cream pt-28 pb-32 px-6 md:px-12 min-h-screen">
      <div className="max-w-3xl mx-auto">
        <Reveal>
          <CheckCircle2 className="text-green mb-4" size={48} />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-green mb-3">Your Buyer Snapshot</p>
          <h1 className="font-serif text-4xl md:text-5xl text-navy tracking-tight mb-6">You're in the "{cat}" stage.</h1>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="bg-white rounded-3xl p-8 border border-black/5 space-y-6">
            <Block title="Recommended next steps" items={
              cat === "Ready to Buy" ? ["Schedule a buyer strategy session", "Set up a personalized home list", "Confirm your preapproval letter"]
              : cat === "Getting Ready" ? ["Complete your lender preapproval", "Refine your target areas & budget", "Tour a few sample homes"]
              : ["Explore counties & communities", "Use the mortgage calculator", "Book a no-pressure discovery call"]} />
            <div>
              <h3 className="font-serif text-xl text-navy mb-2">Recommended areas</h3>
              <p className="text-charcoal/70">{counties.length ? counties.join(", ") + " County" + (counties.length > 1 ? " areas" : "") : "We'll help you compare Metro Atlanta counties."}</p>
            </div>
            <div>
              <h3 className="font-serif text-xl text-navy mb-2">Estimated planning price range</h3>
              <p className="text-charcoal/70">{result.price_range || "To be determined with Richard"}</p>
              <p className="text-xs text-charcoal/50 mt-1">This is a planning range, not a mortgage approval or financial guarantee.</p>
            </div>
            <div>
              <h3 className="font-serif text-xl text-navy mb-2">Suggested resource</h3>
              <Link to="/resources" className="text-gold font-medium underline underline-offset-4">First-Time Homebuyer Guide & Roadmap →</Link>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.2} className="mt-8 flex flex-wrap gap-4">
          <LeadDialog type="buyer" source="buyer-results-list" title="Request a Personalized Home List"
            defaults={{ county: counties.join(", "), price_range: result.price_range }}
            successMsg="Richard will curate a personalized home list and send it your way."
            trigger={<Button data-testid="buyer-request-list" className="bg-gold text-navy font-semibold rounded-full px-7 h-12">Request a Personalized Home List</Button>} />
          <LeadDialog type="consultation" source="buyer-results-consult" title="Schedule a Buyer Consultation"
            trigger={<Button data-testid="buyer-schedule" className="bg-navy text-cream font-semibold rounded-full px-7 h-12">Schedule a Consultation</Button>} />
          <Link to="/search" data-testid="buyer-browse" className="border border-navy/20 text-navy font-semibold px-7 h-12 rounded-full inline-flex items-center gap-2 hover:bg-navy hover:text-cream transition-colors">
            Browse Homes <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
    </div>
  );
}

function Block({ title, items }) {
  return (
    <div>
      <h3 className="font-serif text-xl text-navy mb-3">{title}</h3>
      <ul className="space-y-2">
        {items.map((it) => <li key={it} className="flex items-start gap-2 text-charcoal/75"><CheckCircle2 size={18} className="text-green mt-0.5 shrink-0" />{it}</li>)}
      </ul>
    </div>
  );
}
